import { useCallback } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { type SxProps, type Theme } from "@mui/material/styles";

import { Button } from "ui/button";
import { Checkbox } from "ui/checkbox";
import { Typography } from "ui/typography";
import TouchpointIcon from "containers/admin/clients/touchpoint/components/touchpoint-icon/touchpoint-icon";

import { useCurrentUser } from "hooks/api/use-current-user";

import { Permission } from "types/auth";

import { toSpacedTitleCase } from "utilities";
import { getTouchpointTypeFromMondayChannel } from "utilities/monday-channel";
import { hasPermission } from "utilities/user";

import { MondayTouchpointTemplateDTO } from "generated/schemas";

import { MONDAY_BOARD_URL } from "components/modals/create-campaign-from-template-modal/create-campaign-from-template-modal.constants";

import Icons from "assets";

const STATUS_MESSAGE_SX: SxProps<Theme> = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
  fontWeight: 600,
  "& svg": { width: 16, height: 16 },
};

const TOP_ALIGNED_ROW_SX: SxProps<Theme> = { alignItems: "flex-start" };

const MONDAY_LINK_SX: SxProps<Theme> = (theme) => ({
  ml: 0.5,
  fontSize: theme.typography.caption.fontSize,
  lineHeight: theme.typography.caption.lineHeight,
});

interface TouchpointTemplateItemProps {
  template: MondayTouchpointTemplateDTO;
  invalidTemplateIds: Set<string>;
  existingTouchpointNames?: Set<string>;
  campaignObjectiveDescription: string;
}

export const TouchpointTemplateItem = ({
  template,
  invalidTemplateIds,
  existingTouchpointNames = new Set(),
  campaignObjectiveDescription,
}: TouchpointTemplateItemProps) => {
  const { control } = useFormContext();
  const currentUser = useCurrentUser();
  const isClientUser = hasPermission(currentUser, Permission.PERM_CLIENT_USER);

  const handleOpenMondayBoard = useCallback(
    (templateId: string) => (e: React.MouseEvent) => {
      e.stopPropagation();
      window.open(`${MONDAY_BOARD_URL}/${templateId}`, "_blank", "noopener,noreferrer");
    },
    [],
  );

  if (!template.id) return null;

  const isInvalid = invalidTemplateIds.has(template.id);
  const isAlreadyCreated = template.name ? existingTouchpointNames.has(template.name) : false;
  const isDisabled = isInvalid || isAlreadyCreated;

  return (
    <Controller
      key={template.id}
      name={`templates.${template.id}`}
      control={control}
      render={({ field: { onChange, value, name } }) => (
        <div className="flex items-start">
          <Checkbox
            sx={isDisabled ? TOP_ALIGNED_ROW_SX : undefined}
            size="sm"
            checked={!!value}
            onChange={onChange}
            name={name}
            disabled={isDisabled}
            label={
              <>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    <TouchpointIcon
                      touchpointType={getTouchpointTypeFromMondayChannel(
                        template.touchpointChannel,
                      )}
                      className="size-4 shrink-0"
                    />
                    {template.name}
                  </div>
                  {template.populationType && (
                    <span className="text-gray-500">
                      {toSpacedTitleCase(template.populationType)}
                    </span>
                  )}
                </div>
                {isInvalid && (
                  <div className="flex items-center gap-1">
                    <Typography
                      color="error.main"
                      component="span"
                      sx={STATUS_MESSAGE_SX}
                      title={`Content type "${template.contentType}" is not valid for campaign objective "${campaignObjectiveDescription || "selected campaign objective"}"`}
                      variant="caption">
                      <Icons.ExclamationCircle />
                      Invalid content type
                    </Typography>
                    {!isClientUser && (
                      <Button
                        variant="text"
                        sx={MONDAY_LINK_SX}
                        endIcon={<Icons.Forward className="!size-3" />}
                        onClick={handleOpenMondayBoard(template.id!)}>
                        View on monday.com
                      </Button>
                    )}
                  </div>
                )}
                {isAlreadyCreated && (
                  <div className="flex items-center gap-1">
                    <Typography
                      color="success.dark"
                      component="span"
                      sx={STATUS_MESSAGE_SX}
                      variant="caption">
                      <Icons.CheckCircle />
                      Already created
                    </Typography>
                  </div>
                )}
              </>
            }
          />
        </div>
      )}
    />
  );
};
